import { downloadFile } from "helpers/export-csv";
import { ITEMS_PER_PAGE } from "../../../constants";
import {
  ExportFile,
  SpendAccount,
  useSpendAccountExportLazyQuery,
} from "graphql/generated";
import AccountTable from "pages/programs/all-accounts/account-table";
import { tabs } from "pages/programs/all-accounts/tabs";
import { useEffect, useState } from "react";
import BadgedTabs from "shared-components/badged-tabs";
import Divider from "shared-components/divider";
import ShowingResults from "shared-components/showing-results";
import { SnapIcon } from "suit";
import { SearchInput } from "shared-components/search-input";
import { isNullOrEmpty } from "helpers/null-or-empty";

type AllAccountsProps = {
  accounts: SpendAccount[];
  loadingAccounts: boolean;
};

function AllAccounts({ accounts, loadingAccounts }: AllAccountsProps) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [data, setData] = useState<SpendAccount[]>([]);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [exportReport, { loading: loadingReport, data: exportData }] =
    useSpendAccountExportLazyQuery();

  useEffect(() => {
    if (selectedTab === 0) {
      setData(
        accounts.filter(
          (account: SpendAccount) =>
            account.type === "main" || account.groupStatus === "active"
        )
      );
    } else if (selectedTab === 1) {
      setData(
        accounts.filter(
          (account: SpendAccount) =>
            account.type !== "main" && account.groupStatus !== "active"
        )
      );
    }
  }, [accounts, selectedTab]);

  useEffect(() => {
    if (exportData && exportData.spendAccountExport) {
      handleExport(exportData.spendAccountExport);
    }
    // eslint-disable-next-line
  }, [exportData, loadingReport]);

  useEffect(() => {
    if (!isNullOrEmpty(searchText)) {
      let tempAccounts = [...accounts];
      setData(
        tempAccounts.filter((tempAccount) =>
          tempAccount.name?.toLowerCase()?.includes(searchText.toLowerCase())
        )
      );
    } else {
      setData(accounts);
    }
    // eslint-disable-next-line
  }, [searchText]);

  const handleSort = (selectedTab: string, icon: string) => {
    if (selectedTab === "Account Owner") {
      if (icon === "selector-solid" || icon === "descending-solid") {
        data.sort((a, b) =>
          a.name?.toLowerCase()! < b.name?.toLowerCase()!
            ? -1
            : a.name?.toLowerCase()! > b.name?.toLowerCase()!
            ? 1
            : 0
        );
      } else {
        data.sort((a, b) =>
          a.name?.toLowerCase()! > b.name?.toLowerCase()!
            ? -1
            : a.name?.toLowerCase()! < b.name?.toLowerCase()!
            ? 1
            : 0
        );
      }
    }
    if (selectedTab === "Available Balance") {
      if (icon === "selector-solid" || icon === "descending-solid") {
        data.sort((a, b) =>
          a.available! < b.available! ? -1 : a.available! > b.available! ? 1 : 0
        );
      } else {
        data.sort((a, b) =>
          a.available! > b.available! ? -1 : a.available! < b.available! ? 1 : 0
        );
      }
    }
  };
  const handleExport = (contentToExport: ExportFile) => {
    downloadFile({
      ...contentToExport,
      fileType: "text/csv",
    });
  };

  return (
    <div className="card">
      <h1 className="text-lg font-bold mb-5">All Accounts</h1>
      <BadgedTabs
        tabs={tabs}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />
      <Divider className="lg:hidden" />
      <div className="mt-4">
        <SearchInput setSearchValue={setSearchText} />
      </div>
      <div className="flex justify-between">
        <ShowingResults
          totalNumOfResults={data.length}
          numOfResultsBeingDisplayed={
            data.length <= 10
              ? data.length
              : ITEMS_PER_PAGE * page + 10 >= data.length
              ? data.length
              : ITEMS_PER_PAGE * page + 10
          }
          startingNumOfResults={
            data.length === 0 ? 0 : ITEMS_PER_PAGE * page + 1
          }
        />
        <div
          className="flex items-center text-blue-600 font-bold lg:text-base sm:text-sm cursor-pointer"
          onClick={() =>
            exportReport({
              variables: {
                status: selectedTab === 0 ? "active" : "archived",
              },
            })
          }
        >
          <SnapIcon size="xs" className="pb-1" icon="download-solid" />
          <p className="ml-2">Export as CSV</p>
        </div>
      </div>
      <AccountTable
        accountData={data}
        page={page}
        setPage={setPage}
        handleSort={handleSort}
        loadingAccounts={loadingAccounts}
        itemsPerPage={25}
      />
    </div>
  );
}

export default AllAccounts;
